import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutlined";
import {
	Divider, FormControlLabel, Stack,
	Switch, Typography
} from "@mui/material";
import axios, { type AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import apiRoutes from "../../../../constants/api-routes";
import messageLevels from "../../../../constants/message-levels";
import messages from "../../../../constants/messages";
import { pickPluginDisplayNameFromPayload } from "../../../../helpers/pluginNavHelpers";
import useAuthHeader from "../../../../hooks/useAuthHeader";
import useToastAlert from "../../../../hooks/useToastAlert";
import CoAppEmbeddedVideo from "../../../global/components/CoAppEmbeddedVideo";
import CoAppTextField from "../../../global/components/inputs/CoAppTextField";
import { CoAppActionButton, CoAppLightTooltip } from "../../../global/styled/global.styled";

export interface PluginHomeLocationState {
	name: string;
	pluginId: string;
	platformId?: string | number;
}

interface Plugin {
	active: boolean;
	name?: string;
	pluginId?: string;
	[key: string]: unknown;
}

interface PluginHomePageProps {
	/** Injected by `PluginHomePageWithErrorBoundary` when the route is opened without `navigate(..., { state })`. */
	state?: PluginHomeLocationState | null;
}

export default function PluginHomePage({ state: stateFromBoundary }: PluginHomePageProps = {}) {
	const { pluginId } = useParams<{ pluginId: string }>();
	const { state: stateFromLocation } = useLocation() as { state: PluginHomeLocationState | undefined };
	const state = stateFromBoundary ?? stateFromLocation;
	const platformId = state?.platformId;
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();

	const [endpoint, setEndpoint] = useState<string>("");
	const [plugin, setPlugin] = useState<Plugin>({ active: false });
	const [showThumbnail, setShowThumbnail] = useState<boolean>(false);
	const [showVideo, setShowVideo] = useState<boolean>(false);

	/** Prefer name from GET payload after load; keeps label in sync with sidebar / platforms list. */
	const pluginDisplayName = useMemo(
		() =>
			pickPluginDisplayNameFromPayload(plugin) ?? state?.name ?? `Plugin ${pluginId}`,
		[plugin, state?.name, pluginId],
	);

	useEffect(() => {
		if (pluginId) {
			initPlugin();
		}
	}, [pluginId]);

	const patchPlugin = (patchData: Record<string, unknown>) => {
		const data = {
			patchData: patchData,
			organizationId: plugin.organizationId,
			...(platformId != null ? { platformId } : {}),
		};
		axios.patch(apiRoutes.patchPlugin(pluginId as unknown as string), data, { headers: authHeader })
			.then(() => {
				setPlugin({ ...plugin, ...patchData });
			})
			.catch((err: AxiosError) => {
				console.error("Error patching plugin: ", err);
				handleToastAlert(messageLevels.ERROR, messages.PLUGIN_UPDATED_ERROR_MSG);
			});
	};

	const initPlugin = () => {
		axios.get(apiRoutes.getPlugin(pluginId as unknown as string), { headers: authHeader })
			.then((response) => {
				setPlugin(response.data);
				setEndpoint(response.data.endpoint);
			})
			.catch((err: AxiosError) => {
				console.error(err);
			});
	};

	return (
		<Stack sx={{ padding: "25px" }} spacing={3}>
			<Stack>
				<Stack direction="row" sx={{ justifyContent: "space-between" }}>
					<Typography variant="h3">{`${pluginDisplayName} plugin`}</Typography>
					<FormControlLabel
						label={
							plugin.active
								?
								<Typography variant="body2">Active</Typography>
								:
								<Typography variant="body2">Inactive</Typography>
						}
						labelPlacement="start"
						control={
							<CoAppLightTooltip title={plugin.active ? `Deactivate ${pluginDisplayName}` : `Activate ${pluginDisplayName}`} placement="right">
								<Switch
									checked={Boolean(plugin.active)}
									onChange={() => patchPlugin({ active: !plugin.active })}
								/>
							</CoAppLightTooltip>
						}
					/>
				</Stack>
				<Typography variant="body1">{`The ${pluginDisplayName} plugin is currently ${plugin.active ? "enabled" : "disabled"} for your organization. Manage granular assignment in the groups tab.`}</Typography>
				<Typography variant="body1">{`Manage features of ${pluginDisplayName} below.`}</Typography>
			</Stack>
			<Divider sx={{ width: "56%" }} />
			<Stack>
				<Stack direction="row" spacing={2}>
					<Typography variant="h4">API key status</Typography>
					<CoAppEmbeddedVideo
						showVideo={showVideo}
						setShowVideo={setShowVideo}
						showThumbnail={showThumbnail}
						setShowThumbnail={setShowThumbnail}
						iframeTitle="Plugin API Configuration Video"
						iframeSrc={plugin.instructionalVideoLink}
						thumbnailSrc={plugin.instructionalThumbnailLink}
						thumbnailAltText="Plugin API Configuration Thumbnail"
						thumbnailOverlayText={`How to configure an API key for the ${pluginDisplayName} plugin`}
					/>
				</Stack>
				<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
					{
						plugin.apiKeyConfigured
							?
							<CheckOutlined sx={{ height: "48px", width: "48px", color: "#2E7D32" }} />
							:
							<ErrorOutline sx={{ height: "48px", width: "48px", color: "#DB7A74" }} />
					}
					{
						plugin.apiKeyConfigured
							?
							<Typography variant="body1">An API key has been configured for this plugin.</Typography>
							:
							<Typography variant="body1">An API key has not been configured for this plugin.</Typography>
					}
				</Stack>
			</Stack>
			<Divider sx={{ width: "56%" }} />
			<Stack>
				<Typography variant="h4">API Endpoint</Typography>
				<CoAppTextField
					value={endpoint}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndpoint(e.target.value)}
					label={`${pluginDisplayName} API Endpoint`}
					sx={{ width: "35%" }}
				/>
				<CoAppActionButton onClick={() => patchPlugin({ endpoint: endpoint })} disabled={endpoint.length === 0 || plugin.endpoint === endpoint} sx={{ width: "40px", marginTop: "10px" }}>Save</CoAppActionButton>
			</Stack>
		</Stack >
	);
}